import TableContainer from "@mui/material/TableContainer";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import TableBody from "@mui/material/TableBody";
import Paper from "@mui/material/Paper";
import Pagination from "@mui/material/Pagination";
import StyledTableCell from "../../layouts/table/StyledTableCell";
import StyledTableRow from "../../layouts/table/StyledTableRow";
import { Card, CardContent } from "@mui/material";
import Typography from "@mui/material/Typography";
import ControlPointIcon from "@mui/icons-material/ControlPoint";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Box from "@mui/material/Box";
import React, { useEffect, useMemo, useRef, useState } from "react";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import ReferenceModal from "./ReferenceModal";
import DeleteItemModal from "../DeleteItemModal";
import ReferenceService from "../../../services/reference.service";
import { toast } from "react-toastify";
import { formatPrice } from "../../../helpers/utils";


const initialReference = {
    id: '',
    code: '',
    name: '',
    type: '',
    weight: 0,
    contractorPriceEntresaca: 0,
    contractorPriceTalarasa: 0,
    active: true
}

const REFERENCE_TYPES = ['Rolo', 'Pulpa', 'Pulpa seleccionada', 'Estacon', 'Poste', 'Alfarda'];

const ACTIVE_OPTIONS = [
    { value: 'true', label: 'Activo' },
    { value: 'false', label: 'Inactivo' },
];

const ReferenceList = () => {

    const topRef = useRef(null);
    const [allReferences, setAllReferences] = useState([]);
    const [selectedReference, setSelectedReference] = useState(initialReference);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    // pagination
    const [page, setPage] = useState(0);
    const [limit, setLimit] = useState(10);

    // filters
    const [searchInput, setSearchInput] = useState("");
    const [search, setSearch] = useState("");
    const [typeFilter, setTypeFilter] = useState("");
    const [activeFilter, setActiveFilter] = useState("");

    // modals
    const [openCreateReferenceModal, setOpenCreateReferenceModal] = useState(false);
    const [openEditReferenceModal, setOpenEditReferenceModal] = useState(false);
    const [openDeleteReferenceModal, setOpenDeleteReferenceModal] = useState(false);

    // Debounce search input → search
    useEffect(() => {
        const timer = setTimeout(() => {
            setSearch(searchInput);
            setPage(0);
        }, 300);
        return () => clearTimeout(timer);
    }, [searchInput]);

    // Fetch references when modals close
    useEffect(() => {
        if (openCreateReferenceModal || openEditReferenceModal || openDeleteReferenceModal) return;
        const fetchData = async () => {
            setIsLoading(true);
            setError(null);
            try {
                const list = await ReferenceService.getReferences();
                setAllReferences(Array.isArray(list) ? list : []);
            } catch (e) {
                setError(e.message);
            } finally {
                setIsLoading(false);
            }
        };
        fetchData();
    }, [openCreateReferenceModal, openEditReferenceModal, openDeleteReferenceModal]);

    // Apply filters client-side
    const filteredReferences = useMemo(() => {
        return allReferences.filter((ref) => {
            if (search) {
                const s = search.toLowerCase();
                const matchesSearch =
                    String(ref.code).toLowerCase().includes(s) ||
                    String(ref.name).toLowerCase().includes(s);
                if (!matchesSearch) return false;
            }
            if (typeFilter && ref.type !== typeFilter) return false;
            if (activeFilter !== "") {
                const wantActive = activeFilter === 'true';
                if (Boolean(ref.active) !== wantActive) return false;
            }
            return true;
        });
    }, [allReferences, search, typeFilter, activeFilter]);

    const total = filteredReferences.length;
    const totalPages = Math.max(1, Math.ceil(total / limit));
    const fromRow = total === 0 ? 0 : page * limit + 1;
    const toRow = Math.min((page + 1) * limit, total);

    const paginatedReferences = useMemo(() => {
        return filteredReferences.slice(page * limit, (page + 1) * limit);
    }, [filteredReferences, page, limit]);

    const removeReferenceHandler = async () => {
        try {
            await ReferenceService.removeReference(selectedReference.id);
            toast.success('Se ha eliminado correctamente', { delay: 1000 });
            toggleModal('remove', false);
        } catch (e) {
            console.error(e);
            toast.error('There was an error trying to remove refrence', { delay: 1000 });
        }
    };

    const openCreateReferenceHandler = () => {
        setSelectedReference(initialReference);
        toggleModal('create', true);
    };

    const openEditReferenceHandler = (reference) => {
        setSelectedReference(reference);
        toggleModal('edit', true);
    };

    const openRemoveReferenceHandler = (reference) => {
        setSelectedReference(reference);
        toggleModal('remove', true);
    };

    const toggleModal = (modalType, open) => {
        switch (modalType) {
            case 'create':
                setOpenCreateReferenceModal(open);
                break;
            case 'edit':
                setOpenEditReferenceModal(open);
                break;
            case 'remove':
                setOpenDeleteReferenceModal(open);
                break;
            default:
                console.error(`Modal type ${modalType} is not valid`);
        }
    };

    const handlePaginationChange = (event, newPage) => setPage(newPage - 1);

    const handleLimitChange = (event) => {
        setLimit(parseInt(event.target.value, 10));
        setPage(0);
        // Wait for React to finish re-render before scrolling
        setTimeout(() => {
            if (topRef.current) {
                topRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
            } else {
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }
        }, 0);
    };

    const resetPageAndSet = (setter) => (e) => {
        setter(e.target.value);
        setPage(0);
    };

    const clearFilters = () => {
        setSearchInput("");
        setSearch("");
        setTypeFilter("");
        setActiveFilter("");
        setPage(0);
    };

    const hasActiveFilters = searchInput || typeFilter || activeFilter !== "";

    return (
        <>
            <div ref={topRef} />
            <Card sx={{ overflow: 'hidden' }}>
                <CardContent>
                    {/* Single row: title + filters + actions */}
                    <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                        <Typography variant='h4' sx={{ flexShrink: 0, mr: 1 }}>
                            Referencias
                        </Typography>
                        <TextField
                            label="Buscar (código o nombre)"
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            size="small"
                            sx={{ flex: '1 1 220px', minWidth: 200 }}
                        />
                        <TextField
                            select
                            label="Tipo"
                            value={typeFilter}
                            onChange={resetPageAndSet(setTypeFilter)}
                            size="small"
                            sx={{ minWidth: 160 }}
                        >
                            <MenuItem value="">Todos</MenuItem>
                            {REFERENCE_TYPES.map((t) => (
                                <MenuItem key={t} value={t}>{t}</MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            select
                            label="Estado"
                            value={activeFilter}
                            onChange={resetPageAndSet(setActiveFilter)}
                            size="small"
                            sx={{ minWidth: 140 }}
                        >
                            <MenuItem value="">Todos</MenuItem>
                            {ACTIVE_OPTIONS.map((opt) => (
                                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                            ))}
                        </TextField>
                        {hasActiveFilters && (
                            <Button
                                variant="contained"
                                onClick={clearFilters}
                                sx={{ height: 40 }}
                            >
                                Limpiar filtros
                            </Button>
                        )}
                        <Button
                            variant="contained"
                            startIcon={<ControlPointIcon />}
                            onClick={openCreateReferenceHandler}
                            sx={{ height: 40, whiteSpace: 'nowrap' }}
                        >
                            Añadir nuevo
                        </Button>
                    </Box>

                    {isLoading && allReferences.length <= 0 && <p>Cargando referencias...</p>}
                    {!isLoading && !error && (!Array.isArray(paginatedReferences) || paginatedReferences.length === 0) && (
                        <p>{hasActiveFilters ? 'No se encontraron referencias con esos filtros.' : 'No se han creado referencias.'}</p>
                    )}
                    {error && <p> {error} </p>}
                    {Array.isArray(paginatedReferences) && paginatedReferences.length > 0 &&
                        <TableContainer component={Paper} sx={{ width: '100%', overflowX: 'auto' }}>
                            <Table size="small" sx={{
                                minWidth: 600,
                                '& .MuiTableCell-root': { py: 0.75 },
                            }}>
                                <TableHead>
                                    <TableRow>
                                        <StyledTableCell>Código</StyledTableCell>
                                        <StyledTableCell>Nombre</StyledTableCell>
                                        <StyledTableCell>Tipo</StyledTableCell>
                                        <StyledTableCell>Peso</StyledTableCell>
                                        <StyledTableCell>Precio Entresaca</StyledTableCell>
                                        <StyledTableCell>Precio Talarasa</StyledTableCell>
                                        <StyledTableCell>Estado</StyledTableCell>
                                        <StyledTableCell>Acciones</StyledTableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {paginatedReferences.map((reference) => (
                                        <StyledTableRow
                                            key={reference.id}
                                            sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                        >
                                            <TableCell component="th" scope="row" align="left" sx={{ color: 'primary.main', fontWeight: 600 }}>
                                                {reference.code}
                                            </TableCell>
                                            <TableCell>{reference.name}</TableCell>
                                            <TableCell>{reference.type}</TableCell>
                                            <TableCell align="right">{reference.weight}</TableCell>
                                            <TableCell align="right">{formatPrice(reference.contractorPriceEntresaca)}</TableCell>
                                            <TableCell align="right">{formatPrice(reference.contractorPriceTalarasa)}</TableCell>
                                            <TableCell>{reference.active ? 'Activo' : 'Inactivo'}</TableCell>
                                            <TableCell sx={{ py: 0, whiteSpace: 'nowrap' }}>
                                                <Button size="small" onClick={() => openEditReferenceHandler(reference)} sx={{ minWidth: 36, p: 0.5 }}>
                                                    <EditIcon fontSize="small" />
                                                </Button>
                                                <Button size="small" onClick={() => openRemoveReferenceHandler(reference)} sx={{ minWidth: 36, p: 0.5 }}>
                                                    <DeleteIcon fontSize="small" color={'warning'} />
                                                </Button>
                                            </TableCell>
                                        </StyledTableRow>
                                    ))}
                                </TableBody>
                            </Table>
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                flexWrap: 'wrap',
                                gap: 2,
                                px: 3,
                                py: 2,
                                borderTop: '1px solid',
                                borderColor: 'divider',
                                backgroundColor: (theme) => theme.palette.grey[50],
                            }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 180 }}>
                                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                        Filas por página:
                                    </Typography>
                                    <TextField
                                        select
                                        size="small"
                                        value={limit}
                                        onChange={handleLimitChange}
                                        sx={{
                                            minWidth: 80,
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: 2,
                                            },
                                        }}
                                    >
                                        {[10, 20, 50, 100].map((opt) => (
                                            <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                                        ))}
                                    </TextField>
                                </Box>

                                <Pagination
                                    count={totalPages}
                                    page={page + 1}
                                    onChange={handlePaginationChange}
                                    color="primary"
                                    shape="rounded"
                                    showFirstButton
                                    showLastButton
                                    siblingCount={1}
                                    boundaryCount={1}
                                    sx={{
                                        '& .MuiPaginationItem-root': {
                                            fontWeight: 500,
                                            borderRadius: 2,
                                            margin: '0 2px',
                                            transition: 'all 0.2s ease',
                                            '&:hover': {
                                                backgroundColor: (theme) => `${theme.palette.primary.main}1A`,
                                            },
                                        },
                                        '& .MuiPaginationItem-root.Mui-selected': {
                                            backgroundColor: 'primary.main',
                                            color: 'white',
                                            boxShadow: (theme) => `0 2px 8px ${theme.palette.primary.main}66`,
                                            '&:hover': {
                                                backgroundColor: 'primary.dark',
                                            },
                                        },
                                        '& .MuiPaginationItem-ellipsis': {
                                            color: 'text.secondary',
                                        },
                                    }}
                                />

                                <Typography variant="body2" sx={{ color: 'text.secondary', minWidth: 180, textAlign: 'right' }}>
                                    Mostrando <strong>{fromRow}-{toRow}</strong> de <strong>{total.toLocaleString('es-CO')}</strong>
                                </Typography>
                            </Box>
                        </TableContainer>}
                </CardContent>
            </Card>

            {/*modals*/}
            <ReferenceModal open={openCreateReferenceModal}
                modalType="CREATE"
                reference={selectedReference}
                onClose={() => toggleModal('create', false)}
                referencesSize={allReferences.length} />
            <ReferenceModal open={openEditReferenceModal}
                modalType="UPDATE"
                reference={selectedReference}
                onClose={() => toggleModal('edit', false)}
                referencesSize={allReferences.length} />
            <DeleteItemModal open={openDeleteReferenceModal}
                onClose={() => toggleModal('remove', false)}
                onRemove={removeReferenceHandler}
                itemName={selectedReference.name}
                item={"referencia"} />
        </>
    );
}

export default ReferenceList;
